import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderSelector } from './FolderSelector';
/**
 * Where `folder` may be moved: folders of its own scope (personal and clinic
 * folders never mix) and, for a clinic folder, of its own clinic (the update
 * endpoint rejects any other parent; an admin of two clinics sees both trees),
 * minus the folder itself and its subtree (a move into your own subfolder is a
 * cycle). The current parent stays listed but cannot be submitted (see
 * canSubmit).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function moveTargetsFor(folder, folders) {
    const prune = (nodes) => nodes
        .filter((node) => node.id !== folder.id)
        .map((node) => ({ ...node, children: prune(node.children) }));
    return folder.scope === 'USER'
        ? { personal: prune(folders.personal), clinic: [] }
        : {
            personal: [],
            clinic: prune(folders.clinic.filter((node) => node.clinicId === folder.clinicId)),
        };
}
function countNodes(nodes) {
    return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0);
}
/**
 * The non-drag way to move a folder: pick a new parent from a list. Folder
 * reordering and nesting were drag-and-drop only, which a phone or tablet
 * cannot do. Moving back to the top level is not offered: the update endpoint
 * treats a missing parent as "unchanged", so it cannot express that move.
 */
export function MoveFolderDialog(props) {
    return (_jsx(Dialog, { open: props.open, onOpenChange: props.onOpenChange, children: _jsx(DialogContent, { children: props.open && props.folder && _jsx(MoveFolderDialogBody, { ...props, folder: props.folder }) }) }));
}
function MoveFolderDialogBody({ folder, folders, onOpenChange, onSubmit, }) {
    const { t } = useTranslation();
    const [targetId, setTargetId] = useState(null);
    const targets = moveTargetsFor(folder, folders);
    const hasTargets = countNodes(targets.personal) + countNodes(targets.clinic) > 0;
    const canSubmit = !!targetId && targetId !== folder.parentFolderId;
    return (_jsxs("form", { onSubmit: (e) => {
            e.preventDefault();
            if (canSubmit)
                onSubmit(targetId);
        }, children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: t('folder.moveDialog.title') }), _jsx(DialogDescription, { children: t('folder.moveDialog.description', { name: folder.name }) })] }), _jsx("div", { className: "space-y-2 py-4", children: hasTargets ? (_jsxs(_Fragment, { children: [_jsx(Label, { htmlFor: "move-folder-target", children: t('folder.moveDialog.target') }), _jsx(FolderSelector, { id: "move-folder-target", folders: targets, value: targetId, onChange: (id) => setTargetId(id), placeholder: t('folder.moveDialog.placeholder'), allowRoot: false })] })) : (_jsx("p", { className: "text-muted-foreground text-sm", "data-testid": "move-folder-no-targets", children: t('folder.moveDialog.noTargets') })) }), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => onOpenChange(false), children: t('button.cancel') }), _jsx(Button, { type: "submit", disabled: !canSubmit, "data-testid": "move-folder-submit", children: t('folder.moveDialog.submit') })] })] }));
}
